import { useMemo } from 'react';
import { StatType } from '../subpages/components/CampaignStat';
import { Channels, ConversationStatus } from 'generated-graphql/types';
import { useSearchConversationsTotalCountQuery } from 'generated-graphql/hooks/apollo-hooks';
import { useSatisfiedConversationCount } from './useSatisfiedConversationCount';
import { CampaignType } from 'services/campaign/types';

export const useCampaignStats = (
  campaign: {
    id?: string;
    channel?: Channels | null;
    totalConversationsCount?: number;
    type?: CampaignType | null;
  } | null
) => {
  const campaignId = campaign?.id || '';
  const channel = campaign?.channel;
  const totalConversationsCount = campaign?.totalConversationsCount || 0;

  const { count: satisfiedCount, loading: satisfiedLoading } =
    useSatisfiedConversationCount(campaignId, campaign?.type);

  const baseFilter = useMemo(
    () => ({
      campaignId: { eq: campaignId }
    }),
    [campaignId]
  );

  const { data: deliveredData, loading: deliveredLoading } =
    useSearchConversationsTotalCountQuery({
      variables: {
        filter: {
          ...baseFilter,
          sentAt: { exists: true }
        }
      },
      skip: !campaignId
    });

  const { data: respondedData, loading: respondedLoading } =
    useSearchConversationsTotalCountQuery({
      variables: {
        filter: {
          ...baseFilter,
          sentAt: { exists: true },
          repliesCount: { gt: 0 }
        }
      },
      skip: !campaignId
    });

  const { data: notInterestedData, loading: notInterestedLoading } =
    useSearchConversationsTotalCountQuery({
      variables: {
        filter: {
          ...baseFilter,
          sentAt: { exists: true },
          status: { eq: ConversationStatus.CompletedNotInterested }
        }
      },
      skip: !campaignId
    });

  const { data: optedOutData, loading: optedOutLoading } =
    useSearchConversationsTotalCountQuery({
      variables: {
        filter: {
          ...baseFilter,
          sentAt: { exists: true },
          status: { eq: ConversationStatus.OptedOut }
        }
      },
      skip: !campaignId
    });

  const { data: notDeliveredData, loading: notDeliveredLoading } =
    useSearchConversationsTotalCountQuery({
      variables: {
        filter: {
          ...baseFilter,
          status: { eq: ConversationStatus.DeliveryFailed }
        }
      },
      skip: !campaignId
    });

  const isLoading =
    satisfiedLoading ||
    deliveredLoading ||
    respondedLoading ||
    notInterestedLoading ||
    optedOutLoading ||
    notDeliveredLoading;

  const stats = useMemo(() => {
    const deliveredCount = deliveredData?.searchConversations?.total ?? 0;
    const respondedCount = respondedData?.searchConversations?.total ?? 0;
    const notInterestedCount =
      notInterestedData?.searchConversations?.total ?? 0;
    const optedOutCount = optedOutData?.searchConversations?.total ?? 0;
    const notDeliveredCount = notDeliveredData?.searchConversations?.total ?? 0;

    const getDeliveryRate = () =>
      channel !== Channels.WebChatViaEmail
        ? {
            value: deliveredCount / (totalConversationsCount || 1),
            type: StatType.Delivered
          }
        : null;

    const getCandidates = () => ({
      value: totalConversationsCount,
      type: StatType.Candidates
    });

    const getSatisfied = () => ({
      value: satisfiedCount,
      type: StatType.Satisfied
    });

    const getRespondedRate = () => ({
      value: respondedCount / (deliveredCount || totalConversationsCount || 1),
      type: StatType.Responded
    });

    const getNotDeliveredRate = () => ({
      value: notDeliveredCount / (totalConversationsCount || 1),
      type: StatType.NotDelivered
    });

    const getNotInterestedRate = () => ({
      value:
        notInterestedCount / (deliveredCount || totalConversationsCount || 1),
      type: StatType.NotInterested
    });

    const getOptedOutRate = () => ({
      value: optedOutCount / (deliveredCount || totalConversationsCount || 1),
      type: StatType.OptedOut
    });

    return [
      getCandidates(),
      getSatisfied(),
      getDeliveryRate(),
      getRespondedRate(),
      getNotInterestedRate(),
      getOptedOutRate(),
      getNotDeliveredRate()
    ].filter((stat) => !!stat);
  }, [
    deliveredData,
    respondedData,
    notInterestedData,
    optedOutData,
    notDeliveredData,
    channel,
    totalConversationsCount,
    satisfiedCount
  ]);

  return {
    stats: stats as {
      type: StatType;
      value: number;
    }[],
    isLoading
  };
};
